import { createContext, useCallback, useContext, useMemo, useState, type ReactElement, type ReactNode } from 'react'
import { type CompanyData, type TeamData, type TeamId } from '@terros/common'
import { parseAsTeamId } from '@util'
import { type ConnectionLayerOptions } from 'app/components'
import { useQueryState } from 'nuqs'
import { type MappedTeams } from './types'
import { processTeamLevels } from './util'

type TeamHierarchyContextType = {
  teamLevels: MappedTeams
  teams: TeamData[]
  collapsedTeams: TeamId[]
  toggleTeamCollapse: (teamId: TeamId | TeamId[]) => void
  company: CompanyData
  viewTeam: TeamId | null
  setViewTeam: (teamId: TeamId | null) => void
  connectionSettings: ConnectionLayerOptions
  withPermissions?: boolean
  canEdit?: boolean
  hideLevelLabels?: boolean
}

const TeamHierarchyContext = createContext<TeamHierarchyContextType | undefined>(undefined)

type TeamHierarchyProviderProps = {
  children: ReactNode
  teams: TeamData[]
  company: CompanyData
  connectionSettings: ConnectionLayerOptions
  withPermissions?: boolean
  canEdit?: boolean
  hideLevelLabels?: boolean
}

export const TeamHierarchyProvider = ({
  children,
  teams,
  company,
  connectionSettings,
  withPermissions,
  canEdit,
  hideLevelLabels,
}: TeamHierarchyProviderProps): ReactElement => {
  const [collapsedTeams, setCollapsedTeams] = useState<TeamId[]>([])

  const [viewTeam, setViewTeam] = useQueryState('viewTeam', parseAsTeamId)

  const toggleTeamCollapse = useCallback((teamId: TeamId | TeamId[]): void => {
    const teamIds = Array.isArray(teamId) ? teamId : [teamId]
    setCollapsedTeams((prev) => {
      if (prev.some((id) => teamIds.includes(id))) {
        return prev.filter((id) => !teamIds.includes(id))
      }
      return [...prev, ...teamIds]
    })
  }, [])

  const teamLevels = useMemo(
    () => processTeamLevels(teams ?? [], collapsedTeams, company),
    [teams, collapsedTeams, company]
  )

  const contextValue = useMemo(
    () => ({
      teamLevels,
      teams: teams ?? [],
      collapsedTeams,
      toggleTeamCollapse,
      company,
      viewTeam,
      setViewTeam,
      connectionSettings,
      withPermissions,
      canEdit,
      hideLevelLabels,
    }),
    [
      teamLevels,
      teams,
      collapsedTeams,
      toggleTeamCollapse,
      company,
      viewTeam,
      setViewTeam,
      connectionSettings,
      withPermissions,
      canEdit,
      hideLevelLabels,
    ]
  )

  return <TeamHierarchyContext.Provider value={contextValue}>{children}</TeamHierarchyContext.Provider>
}

export const useTeamHierarchyContext = (): TeamHierarchyContextType => {
  const context = useContext(TeamHierarchyContext)
  if (context === undefined) {
    throw new Error('useTeamHierarchyContext must be used within a TeamHierarchyProvider')
  }
  return context
}
